import React, { useState, useMemo } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Card, Table, Tag, Input, Space, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';

const { Search } = Input;

/**
 * TopProductsTable - 熱銷商品表格組件
 *
 * @param {Object} props
 * @param {Array} props.products - 商品列表
 * @param {Object} props.channelMap - 通路映射 { id: { name, code } }
 */
const TopProductsTable = ({ products = [], channelMap = {}, metadata = {} }) => {
  const { t } = useTranslation();
  const cs = metadata?.currency_symbol || '$';
  const dp = metadata?.currency_decimal_places ?? 2;
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  // 篩選商品
  const filteredProducts = useMemo(() => {
    if (!searchText) return products;

    const lowerSearch = searchText.toLowerCase();
    return products.filter(
      (p) =>
        p.name?.toLowerCase().includes(lowerSearch) || p.sku?.toLowerCase().includes(lowerSearch)
    );
  }, [products, searchText]);

  // 依通路分組
  const productsByChannel = useMemo(() => {
    const grouped = {};

    products.forEach((product) => {
      Object.entries(product.channels || {}).forEach(([channelId, quantity]) => {
        if (!grouped[channelId]) {
          grouped[channelId] = [];
        }
        grouped[channelId].push({
          ...product,
          channel_quantity: quantity,
        });
      });
    });

    // 每個通路按銷量排序
    Object.keys(grouped).forEach((channelId) => {
      grouped[channelId].sort((a, b) => b.channel_quantity - a.channel_quantity);
    });

    return grouped;
  }, [products]);

  const columns = [
    {
      title: t('shared.analytics.table.rank'),
      key: 'rank',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: t('shared.analytics.table.sku'),
      dataIndex: 'sku',
      key: 'sku',
      width: 150,
    },
    {
      title: t('shared.analytics.table.product_name'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: t('shared.analytics.table.total_quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      sorter: (a, b) => a.quantity - b.quantity,
      render: (value) => value?.toLocaleString() || 0,
    },
    {
      title: t('shared.analytics.table.total_revenue'),
      dataIndex: 'revenue',
      key: 'revenue',
      width: 150,
      sorter: (a, b) => a.revenue - b.revenue,
      render: (value) => `${cs}${value?.toFixed(dp) || (dp > 0 ? '0.' + '0'.repeat(dp) : '0')}`,
    },
    {
      title: t('shared.analytics.table.channel_distribution'),
      key: 'channels',
      render: (_, record) => (
        <Space size={[0, 8]} wrap>
          {Object.entries(record.channels || {})
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3)
            .map(([channelId, quantity]) => {
              const channel = channelMap[channelId];
              if (!channel) return null;
              return (
                <Tag key={channelId} color="blue">
                  {channel.name}: {quantity}
                </Tag>
              );
            })}
        </Space>
      ),
    },
  ];

  // 通路專屬欄位（顯示該通路的銷量）
  const channelColumns = [
    ...columns.slice(0, 3), // rank, sku, name
    {
      title: t('shared.analytics.table.channel_quantity'),
      dataIndex: 'channel_quantity',
      key: 'channel_quantity',
      width: 120,
      sorter: (a, b) => a.channel_quantity - b.channel_quantity,
      render: (value) => value?.toLocaleString() || 0,
    },
    columns[4], // revenue
  ];

  const tabItems = [
    {
      key: 'all',
      label: t('shared.analytics.top_products.tab_all'),
      children: (
        <Table
          dataSource={filteredProducts}
          columns={columns}
          rowKey="id"
          pagination={false}
          scroll={{ x: 900 }}
          size="small"
        />
      ),
    },
    ...Object.entries(productsByChannel)
      .map(([channelId, channelProducts]) => {
        const channel = channelMap[channelId];
        if (!channel) return null;
        return {
          key: channelId,
          label: channel.name,
          children: (
            <Table
              dataSource={channelProducts}
              columns={channelColumns}
              rowKey={(record) => `${record.id}-${channelId}`}
              pagination={false}
              scroll={{ x: 800 }}
              size="small"
            />
          ),
        };
      })
      .filter(Boolean),
  ];

  return (
    <Card
      title={t('shared.analytics.top_products.title')}
      size="small"
      extra={
        <Search
          placeholder={t('shared.analytics.top_products.search_placeholder')}
          allowClear
          style={{ width: 250 }}
          onSearch={setSearchText}
          onChange={(e) => setSearchText(e.target.value)}
          prefix={<SearchOutlined />}
        />
      }
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </Card>
  );
};

export default TopProductsTable;
